import configJSON from 'config.json';
import { SITE_LOCATION_COOKIE_NAME } from 'constants/cookies';
import { IncomingMessage } from 'http';
import { CountryCode, CountryCodeList } from 'types/countries';
import { getStringFromRequestCookie } from './cookies';
import { FeaturesType } from 'contexts/features';
import isUserAgentCrawler from './crawler-agent';
import { getFeaturesOverrideFromRequest } from 'utils/features';
import { TenantId } from '@tenants/types';
import { getTenantFromRequestHeader, TenantTypes } from '@tenants/manager';
import { isTenantExpCA, isTenantExpInService, isTenantExpUS, isZoocasaTenant } from '@tenants/utils/which-tenant';

/**
 * Gets a valid site location by checking against the tenant's localization configuration.
 * @param storedSiteLocation The stored site location to validate.
 * @param tenant The tenant object containing configuration.
 * @returns The valid country code.
 */
function getValidSiteLocationForTenant(storedSiteLocation: CountryCode, tenant: TenantTypes): CountryCode {
  if (tenant.localization.countries.includes(storedSiteLocation)) {
    return storedSiteLocation;
  } else {
    // If stored location is not allowed, return the default country
    return tenant.localization.defaultCountry;
  }
}

/**
 * Retrieves the site location based on the incoming request.
 * @param request The incoming HTTP request object.
 * @param userCountryCode The user location country code.
 * @returns The country code representing the site location. Defaults to `CountryCodeList.CANADA`.
 */
export function getSiteLocationFromRequest(request?: IncomingMessage, userCountryCode = CountryCodeList.CANADA, defaultTenantId = configJSON.defaultTenantId as TenantId): CountryCode {
  const storedSiteLocation: CountryCode | undefined = getStringFromRequestCookie(SITE_LOCATION_COOKIE_NAME, request) as CountryCode;

  const isCrawler = isUserAgentCrawler(request?.headers);
  const features = getFeaturesOverrideFromRequest(request) as unknown as FeaturesType;
  const tenant = getTenantFromRequestHeader(request, defaultTenantId);

  // Check if there's a stored site location and validate it against tenant's localization config
  if (storedSiteLocation) {
    return getValidSiteLocationForTenant(storedSiteLocation, tenant);
  }

  // Check URL path first
  if (request?.url?.startsWith('/us')) {
    return CountryCodeList.UNITED_STATES;
  }

  // Handle crawler behavior
  if (isCrawler) {
    return CountryCodeList.CANADA;
  }

  // Handle theme overrides
  if (isTenantExpCA(tenant.id, features)) {
    return CountryCodeList.CANADA;
  } else if (isTenantExpUS(tenant.id, features)) {
    return CountryCodeList.UNITED_STATES;
  } else if (isTenantExpInService(tenant.id, features)) {
    return CountryCodeList.UNITED_STATES;
  }

  // Handle feature overrides
  if (features?.useUsLocation === true) {
    return CountryCodeList.UNITED_STATES;
  }

  // Handle ZOOCASA theme with user location
  if (isZoocasaTenant(tenant.id, features)) {
    if (userCountryCode === CountryCodeList.CANADA) {
      return CountryCodeList.CANADA;
    } else if (userCountryCode === CountryCodeList.UNITED_STATES) {
      return CountryCodeList.UNITED_STATES;
    }
  }

  // Default to Canada
  return CountryCodeList.CANADA;
}
